// src/components/MobileNav.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  X,
  Home,
  Battery,
  Plus,
  Settings,
  Users,
  FileText,
  LogOut,
  User,
  Bell,
  Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import '../styles/MobileNav.css';

export default function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser, logout, isAdmin } = useAuth();

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
      setIsOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  // Don't show navigation on landing, login, signup pages
  const hideNav = ['/', '/login', '/signup'].includes(location.pathname);

  if (hideNav || !currentUser) {
    return (
      <nav className="mobile-nav-minimal">
        <LanguageSwitcher />
      </nav>
    );
  }

  return (
    <>
      {/* Header Bar */}
      <header className="mobile-nav-header">
        <div className="mobile-nav-header-content">
          <Link to="/dashboard" className="mobile-nav-logo">
            <img src="/logo-primary.png" alt="VoltFox" className="mobile-nav-logo-img" />
          </Link>

          <div className="mobile-nav-actions">
            <LanguageSwitcher />
            <button
              className="mobile-nav-burger"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Overlay */}
      {isOpen && (
        <div
          className="mobile-nav-overlay"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-out Menu */}
      <aside className={`mobile-nav-sidebar ${isOpen ? 'mobile-nav-sidebar-open' : ''}`}>
        <div className="mobile-nav-sidebar-content">
          {/* User Info */}
          {currentUser && (
            <div className="mobile-nav-user">
              <div className="mobile-nav-user-avatar">
                <User size={24} />
              </div>
              <div className="mobile-nav-user-info">
                <p className="mobile-nav-user-email">{currentUser.email}</p>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="mobile-nav-links">
            <Link
              to="/dashboard"
              className={`mobile-nav-link ${isActive('/dashboard') ? 'mobile-nav-link-active' : ''}`}
            >
              <Home size={20} />
              <span>{t('nav.dashboard')}</span>
            </Link>

            <Link
              to="/devices"
              className={`mobile-nav-link ${isActive('/devices') ? 'mobile-nav-link-active' : ''}`}
            >
              <Battery size={20} />
              <span>{t('nav.devices')}</span>
            </Link>

            <Link
              to="/add-device"
              className={`mobile-nav-link ${isActive('/add-device') ? 'mobile-nav-link-active' : ''}`}
            >
              <Plus size={20} />
              <span>{t('nav.addDevice')}</span>
            </Link>

            <Link
              to="/community"
              className={`mobile-nav-link ${isActive('/community') ? 'mobile-nav-link-active' : ''}`}
            >
              <Users size={20} />
              <span>{t('nav.community')}</span>
            </Link>

            <Link
              to="/insurance"
              className={`mobile-nav-link ${isActive('/insurance') ? 'mobile-nav-link-active' : ''}`}
            >
              <FileText size={20} />
              <span>{t('nav.insurance')}</span>
            </Link>

            <div className="mobile-nav-divider" />

            <Link
              to="/notifications"
              className={`mobile-nav-link ${isActive('/notifications') ? 'mobile-nav-link-active' : ''}`}
            >
              <Bell size={20} />
              <span>{t('nav.notifications')}</span>
            </Link>

            <Link
              to="/settings"
              className={`mobile-nav-link ${isActive('/settings') ? 'mobile-nav-link-active' : ''}`}
            >
              <Settings size={20} />
              <span>{t('nav.settings')}</span>
            </Link>

            {/* Admin Link - Only visible for admins */}
            {isAdmin && (
              <>
                <div className="mobile-nav-divider" />
                <Link
                  to="/admin"
                  className={`mobile-nav-link ${isActive('/admin') ? 'mobile-nav-link-active' : ''}`}
                >
                  <Shield size={20} />
                  <span>{t('nav.admin')}</span>
                </Link>
              </>
            )}

            {/* Debug: Show isAdmin status in development */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: '#999' }}>
                Debug: isAdmin = {isAdmin ? 'true' : 'false'}
              </div>
            )}
          </nav>

          {/* Sign Out */}
          <div className="mobile-nav-footer">
            <button
              onClick={handleSignOut}
              className="mobile-nav-signout"
            >
              <LogOut size={20} />
              <span>{t('nav.signOut')}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
